import { useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { MeasurementsGraph } from '../../components/measurement-graph/measurementsGraph.tsx';
import { PageTitle } from '../../components/pageTitle.tsx';
import { ProtocolPdf } from '../../components/protocol-pdf/protocolPdf.tsx';
import { VisualInspectionImage } from '../../components/visualInspectionImage.tsx';
import { useConfirmOnLeave } from '../../hooks/useConfirmOnLeave.ts';
import { Record, RecordDraft } from '../../types/sharedTypeImpl.ts';
import { trpc } from '../../utils/trpc/trpc.ts';
import { RecordForm } from './recordForm.tsx';
import { useFetchPrevRecords } from './useFetchPrevRecords.ts';
import { useInitRecord } from './useInitRecord.ts';
import { useLoadProtocol } from './useLoadProtocol.ts';
import { useMGraphAdapter } from './useMGraphAdapter.ts';
import { usePageSettings } from './usePageSettings.ts';
import { useRecordSave } from './useRecordSave.tsx';


export function RecordView() {
    const maxGraphDataPoints = 15;

    const [record, setRecord] = useState<RecordDraft | null>(null);
    const [selectedPosNr, setSelectedPosNr] = useState<string | null>(null);
    const [showSelectedPosNr, setShowSelectedPosNr] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const isFinalSavedRef = useRef(false);


    const location = useLocation();
    const navigate = useNavigate();

    const {
        isViewMode,
        initialRecordId,
        onNavigateRecord,
    } = usePageSettings();

    const {
        protocol,
        pdfFetch,
        positionImages,
    } = useLoadProtocol(record);

    const { isFinishedRecord } = useInitRecord(initialRecordId, record, setRecord, !isViewMode);

    const { recentRecords, prevRecord, nextRecord } = useFetchPrevRecords({
        record: record ?? undefined,
        protocol,
        limit: maxGraphDataPoints - 1,
    });

    const { data: cmmConfigs } = trpc.cmmConfigs.get.useQuery({ sort: { order: 1 } });

    const {
        isSaving,
        draftSaveError,
        handleDraftSave,
        handleFinalSave,
    } = useRecordSave({
        record,
        protocol,
        isViewMode,
        isFinishedRecord,
        isDirty,
        setIsDirty,
        isFinalSavedRef,
        onFinalSaved: () => handleClose(),
    });

    const { measurements, controlValues } = useMGraphAdapter({
        protocol,
        prevRecords: recentRecords,
        currentRecord: record,
        selectedPosNr,
        maxDataPoints: maxGraphDataPoints,
    });
    useConfirmOnLeave(() => isDirty && !isFinalSavedRef.current, !isViewMode);


    const handleClose = () => {
        const returnPath = location.state?.returnTo;
        const fallbackPath = record?.measurementType === 'ioqc' ? '/ioqc-list' : '/record-list'
        navigate(returnPath ?? fallbackPath);
    }

    const handleNavigateRecord = (record: Record) => {
        if (isSaving || isDirty) {
            const confirmLeave = window.confirm('You have unsaved changes. Do you want to leave?');
            if (!confirmLeave) {
                return;
            }
        }
        onNavigateRecord(record._id!);
        setIsDirty(false);
    }


    const selectedPosition = selectedPosNr != null
        ? protocol?.positions.find(it => it.positionNumber === selectedPosNr)
        : null;

    const positionSelectionObj = useMemo(() => {
        if (selectedPosition == null) return null;
        const idx = protocol!.positions.findIndex(it => it.positionNumber === selectedPosition.positionNumber);
        return { positionIdx: idx };
    }, [selectedPosition, protocol]);

    const visiblePositionIds = new Set(protocol?.positions.map(position => position.id) ?? []);

    return (
        <>
            <PageTitle title='Record' />
            <div className='measurement-container'>
                <ProtocolPdf
                    className='measurement-main-pane measurement-pdf-container'
                    isViewMode={true}
                    pdfContent={pdfFetch.data ?? null}
                    isPdfLoading={pdfFetch.isLoading}
                    isPdfError={!!pdfFetch.error}
                    positions={protocol?.positions}
                    visiblePositionIds={visiblePositionIds}
                    positionSelection={positionSelectionObj}
                    showSelectedPosNr={showSelectedPosNr}
                />
                <div className='measurement-side-pane measurement-side-pane--record measurement-form-pane'>
                    {protocol && record &&
                        <RecordForm
                            className='measurement-form-top'
                            protocol={protocol}
                            record={record}
                            setRecord={setRecord}
                            selectedPosNr={selectedPosNr}
                            setSelectedPosNr={setSelectedPosNr}
                            showSelectedPosNr={showSelectedPosNr}
                            setShowSelectedPosNr={setShowSelectedPosNr}
                            isDirty={isDirty}
                            setIsDirty={setIsDirty}
                            isFinishedRecord={isFinishedRecord}
                            isViewMode={isViewMode}
                            isSaving={isSaving}
                            draftSaveError={draftSaveError}
                            prevRecord={prevRecord}
                            nextRecord={nextRecord}
                            cmmConfigs={cmmConfigs}
                            onDraftSave={handleDraftSave}
                            onFormSubmit={handleFinalSave}
                            onCloseClicked={handleClose}
                            onNavigateRecord={handleNavigateRecord}
                        />
                    }

                    {selectedPosition?.type === 'measurement' &&
                        <MeasurementsGraph
                            className='measurement-form-bottom'
                            measurements={measurements}
                            controlValues={controlValues}
                            openDialogOnClick={true}
                        />
                    }

                    {selectedPosition?.type === 'visualInspection' &&
                        <VisualInspectionImage
                            className='measurement-form-bottom'
                            imageData={positionImages.get(selectedPosition.positionNumber)}
                            isViewMode={true}
                        />
                    }
                </div>
            </div>
        </>
    );
}
